<template>
  <div>
    <RechargeListFiltersAdmin />

    <v-card outlined>
      <v-data-table
        disable-sort
        hide-default-footer
        mobile-breakpoint="20"
        :headers="headers"
        :items="rechargesAdmin"
        :loading="isLoading"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
      >
        <template v-slot:[`item.code`]="{ item }">
          <v-chip small>
            <span class="caption">{{ item.code }}</span>
          </v-chip>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          <div class="d-flex" v-if="item.type === 'ASAAS_BOLETO'">
            <v-icon color="grey lighten-2" left>mdi-barcode</v-icon>
            <div class="align-self-center ml-2">
              {{ $t('recharge.billet.title') }}
            </div>
          </div>
          <div class="d-flex" v-if="item.type === 'ASAAS_CREDIT_CARD'">
            <v-icon color="grey lighten-2" left>mdi-credit-card-outline</v-icon>
            <div class="align-self-center ml-2">
              {{ $t('recharge.card.title') }}
            </div>
          </div>
          <div class="d-flex" v-if="item.type === 'ASAAS_PIX'">
            <PixIcon width="30" height="20" opacity="0.15" />
            <div class="align-self-center ml-2">
              {{ $t('recharge.pix.title') }}
            </div>
          </div>
        </template>
        <template v-slot:[`item.real_estate`]="{ item }">
          <v-tooltip v-if="item.real_estate" top>
            <template v-slot:activator="{ on, attrs }">
              <span  class="d-block text-truncate mw-200" v-bind="attrs" v-on="on">
                {{ customer(item) }}
              </span>
            </template>
            {{ customer(item) }}
          </v-tooltip>
        </template>
        <template v-slot:[`item.comment`]="{ item }">
          <v-tooltip v-if="item.comment" top>
            <template v-slot:activator="{ on, attrs }">
              <span  class="d-block text-truncate mw-150" v-bind="attrs" v-on="on">
                {{ item.comment }}
              </span>
            </template>
            {{ item.comment }}
          </v-tooltip>
        </template>
        <template v-slot:[`item.date_created`]="{ item }">
          {{ date(item.date_created) }}
        </template>
        <template v-slot:[`item.due_date`]="{ item }">
          {{ formatDueDate(item.due_date) }}
        </template>
        <template v-slot:[`item.value`]="{ item }">
          {{ money(item.value) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="handleStatusColor(item.status)" small>
            {{ $t(`recharge.status.${item.status}`) }}
          </v-chip>
        </template>
        <template v-slot:[`item.attachments`]="{ item }">
          <v-btn
            v-if="item.status !== 'CONFIRMED' && item.type === 'ASAAS_BOLETO'"
            :href="item.external_doc"
            target="_blank"
            color="primary"
            elevation="0"
            download
            outlined
            small
          >
            <v-icon left small>
              mdi-tray-arrow-down
            </v-icon>
            <span class="caption">
              {{ $t('recharge.billet.billetDownload') }}
            </span>
          </v-btn>
          <span v-else class="grey--text text--lighten-1">---</span>
        </template>
        <template v-slot:[`item.advanced_balance`]="{ item }">
          <div v-if="!item.advanced_balance">
            <v-btn
              v-if="isRechargePendingAndBillet(item)"
              @click="openConfirmAdvanceBalanceModal(item)"
              small
              outlined
              color="primary"
            >
              <v-icon small left>
                mdi-cash-fast
              </v-icon>
              <span class="caption">
                {{ $t('recharge.label.advanceBalance') }}
              </span>
            </v-btn>
            <span v-else class="grey--text text--lighten-1">---</span>
          </div>
          <div class="d-flex align-center justify-center" v-else>
            <v-chip small color="green lighten-4">
              <span>
                {{ $t(`recharge.label.advanced`) }}
              </span>
            </v-chip>
            <v-tooltip v-if="isRechargePendingAndBillet(item)" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs" v-on="on"
                  @click="openConfirmUndoAdvanceBalanceModal(item)"
                  small
                  icon
                  color="error"
                  class="ml-1"
                >
                  <v-icon small>
                    mdi-cash-refund
                  </v-icon>
                </v-btn>
              </template>
              {{ $t('recharge.label.undoAdvanceBalance') }}
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <h-pagination :fetch="fetchRechargesAdmin" />
    <ModalConfirmAdvanceBalance :selectedRecharge="selectedRecharge" />
    <ModalConfirmUndoAdvanceBalance :selectedRecharge="selectedRecharge" />
  </div>
</template>

<script>
import PixIcon from '@/components/icons/Pix'
import RechargeListFiltersAdmin from '@/components/recharge/RechargeListFiltersAdmin'
import ModalConfirmAdvanceBalance from '@/components/recharge/modals/ModalConfirmAdvanceBalance'
import ModalConfirmUndoAdvanceBalance from '@/components/recharge/modals/ModalConfirmUndoAdvanceBalance'
import { mapGetters, mapActions } from 'vuex'
import { rechargeHeadersAdmin } from '../../views/listHeaders/rechargeHeadersAdmin'
import { catalogMonetary, catalogDate, catalogDateWithoutTime } from '@/util/catalog'
import { handleColors } from '@/util/colors'

export default {
  name: 'RechargeListAdmin',

  components: {
    PixIcon,
    RechargeListFiltersAdmin,
    ModalConfirmAdvanceBalance,
    ModalConfirmUndoAdvanceBalance,
  },

  data() {
    return {
      headers: rechargeHeadersAdmin,
      money: catalogMonetary,
      date: catalogDate,
      formatDueDate: catalogDateWithoutTime,
      statusColor: {
        PENDING: 'grey lighten-2',
        RECEIVED: 'blue lighten-3',
        CONFIRMED: 'green lighten-3',
      },
      selectedRecharge: null,
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'rechargesAdmin', 'itemsPerPage']),
  },

  methods: {
    ...mapActions(['fetchRechargesAdmin', 'openModal']),

    handleStatusColor(status) {
      return handleColors(status)
    },

    openConfirmAdvanceBalanceModal(recharge) {
      this.selectedRecharge = recharge
      this.openModal('ADVANCE_BALANCE_CONFIRM_MODAL')
    },

    openConfirmUndoAdvanceBalanceModal(recharge) {
      this.selectedRecharge = recharge
      this.openModal('UNDO_ADVANCE_BALANCE_CONFIRM_MODAL')
    },

    isRechargePendingAndBillet(recharge) {
      return recharge.type === 'ASAAS_BOLETO' && recharge.status == 'PENDING'
    },

    customer(recharge) {
      if (!recharge.real_estate) return '---'
      return recharge.real_estate.name
    },

  },

  mounted() {
    this.fetchRechargesAdmin()
  },
}
</script>
